/**
 * Which scheduling mode a plan runs on, as the SYMBOL alone: the calendar, or the loop.
 *
 * Owner's polish of 4 November 2026: the word beside the mark (`Kalendarz` / `Własnym tempem`) went
 * from every list row, and the inspector's plate carries the same mark at the right end of its
 * figures. The word is still the mark's name — hidden, so a screen reader and a search over the row's
 * text both find it, and as the pointer's tooltip — because a symbol nobody can name is a picture
 * rather than a fact.
 *
 * Three callers draw it (both list rows and the hero), which is why the choice of glyph is made here
 * once. The marker is an allow-list on `SelfPaced` (see `model/planLibraryRow`): an unrecognised mode
 * carries no marker and draws the calendar, the same silence a weekly plan has.
 */

import { useTranslation } from 'react-i18next';

import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { PlanSchedulingMarker } from '../model/planLibraryRow';
import { CalendarModeIcon, SelfPacedModeIcon } from './PlanLibraryIcons';

interface PlanModeMarkProps {
  readonly marker: PlanSchedulingMarker | null;
  /** The mark's box. The glyph's size is the caller's, set on this box's `svg`. */
  readonly className?: string;
}

export function PlanModeMark({ marker, className }: PlanModeMarkProps) {
  const { t } = useTranslation();
  const name = t(
    marker === 'selfPaced' ? 'web:planLibrary.mode.selfPaced' : 'web:planLibrary.mode.calendar',
  );
  const Icon = marker === 'selfPaced' ? SelfPacedModeIcon : CalendarModeIcon;

  return (
    <span className={className} title={name}>
      <Icon />
      <span className={hidden.srOnly}>{name}</span>
    </span>
  );
}
